import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { toast } from 'sonner';
import { useSEO, buildPropertySchema, buildBreadcrumbSchema } from '../utils/seo';
import { getPropertyCurrency, formatConvertedPrice, formatAreaSize } from '../utils/propertyPrice';
import AIDisclaimer from '../components/AIDisclaimer';
import OwnerClaimBanner from '../components/OwnerClaimBanner';
import { EpcBar, TenureBadge, FurnishedBadge, PropertyTypeBadge, CouncilTaxBadge } from '../components/PropertyBadges';
import PanoramicViewer from '../components/property/PanoramicViewer';
import PropertySidebar from '../components/property/PropertySidebar';
import FloorPlansSection from '../components/FloorPlansSection';
import ReferralForm from '../components/property/ReferralForm';
import LocalProsPanel from '../components/LocalProsPanel';
import ListingBadges from '../components/ListingBadges';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { 
  MapPin, Bed, Bath, Square, Heart, HeartOff, Share2, 
  Calendar as CalendarIcon, Sparkles, ChevronLeft,
  ChevronRight, Loader2, Shield,
  Maximize, X, Copy, MessageCircle, ArrowRight
} from 'lucide-react';
import logger from '../utils/logger';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [access, setAccess] = useState({ viewing_access: false, buyer_verification_access: false, admin_approved: false });
  const [sellerContact, setSellerContact] = useState(null);
  const [contactRequestStatus, setContactRequestStatus] = useState('none');
  
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showPanoramic, setShowPanoramic] = useState(false);
  
  const [aiValuation, setAiValuation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [bookingTime, setBookingTime] = useState('');
  const [bookingMessage, setBookingMessage] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Currency: derived from context + property data (live Fixer.io rates)
  const propertyCurrency = property ? getPropertyCurrency(property) : 'GBP';
  const propSymbol = { GBP: '£', INR: '₹', CAD: 'C$', USD: '$', EUR: '€' }[propertyCurrency] || '£';

  const [sellerMatchFee, setSellerMatchFee] = useState(45);
  const [sellerRentFee, setSellerRentFee] = useState(5);
  const [buyerFee, setBuyerFee] = useState(20);
  const [professionalFee, setProfessionalFee] = useState(25);
  const [tenantFee, setTenantFee] = useState(0);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralType, setReferralType] = useState('');
  const { country: selectedCountry, countryInfo, fees: countryFees, exchangeRates } = useCountry();

  // Sync fees with country selector
  useEffect(() => {
    setSellerMatchFee(countryFees.seller);
    setSellerRentFee(countryFees.sellerRent);
    setBuyerFee(countryFees.buyer);
    setProfessionalFee(countryFees.pro);
  }, [countryFees]);

  useSEO({
    title: property ? `${property.title} — ${property.ref_number || ''} | ${property.city} | ${property.listing_type === 'rent' ? 'For Rent' : 'For Sale'}` : 'Property Details',
    description: property ? `Ref: ${property.ref_number || 'N/A'} — ${property.bedrooms || 0} bed ${property.property_type || 'property'} ${property.listing_type === 'rent' ? 'for rent' : 'for sale'} in ${property.city}, ${property.country}. ${propSymbol}${property.price?.toLocaleString('en-GB')}. View 360° tours, AI valuation & book viewings on Shopperden.` : '',
    canonical: property ? `https://shopperden.com/property/${id}` : undefined,
    type: 'article',
    image: property?.images?.[0] || undefined,
    schema: property
      ? [
          buildPropertySchema({ ...property, id }),
          buildBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Properties', url: '/properties' },
            { name: property.city, url: `/properties?city=${encodeURIComponent(property.city || '')}` },
            { name: property.title, url: `/property/${id}` },
          ]),
        ].filter(Boolean)
      : undefined,
  });

  const fetchProperty = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/properties/${id}`);
      setProperty(response.data);
      if (response.data.ai_valuation) setAiValuation(response.data.ai_valuation);
    } catch (error) {
      toast.error('Property not found');
      navigate('/properties');
    } finally { setLoading(false); }
  }, [id, navigate]);

  const checkFavorite = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/favorites`);
      setIsFavorite(response.data.some(p => p.id === id));
    } catch (error) {
      logger.error('Failed to check favorites:', error);
    }
  }, [id]);

  const checkAccess = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/access/${id}`);
      setAccess(response.data);
      if (response.data.buyer_verification_access) {
        const reqRes = await axios.get(`${API_URL}/api/contact-request/${id}/status`);
        setContactRequestStatus(reqRes.data.admin_status);
        if (reqRes.data.admin_status === 'approved') {
          try {
            const contactRes = await axios.get(`${API_URL}/api/properties/${id}/contact`);
            setSellerContact(contactRes.data);
          } catch (e) {
            logger.error('Failed to fetch seller contact:', e);
          }
        }
      }
    } catch (error) {
      logger.error('Failed to check access:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchProperty();
    if (user) { checkFavorite(); checkAccess(); }
  }, [id, user, fetchProperty, checkFavorite, checkAccess]);

  const toggleFavorite = async () => {
    if (!user) { toast.error('Please login'); navigate('/login'); return; }
    try {
      if (isFavorite) {
        await axios.delete(`${API_URL}/api/favorites/${id}`);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API_URL}/api/favorites/${id}`, {});
        toast.success('Added to favorites');
      }
      setIsFavorite(!isFavorite);
    } catch (error) { toast.error('Failed to update favorites'); }
  };

  const handlePayment = async (paymentType) => {
    if (!user) { toast.error('Please login'); navigate('/login'); return; }
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/create-checkout`,
        { property_id: id, payment_type: paymentType, origin_url: window.location.origin },
        
      );
      
      // Mock payment — no Stripe redirect, go straight to success
      if (response.data.mock) {
        toast.success('Payment simulated successfully!');
        navigate(`/payment-success?session_id=${response.data.session_id}`);
        return;
      }
      
      // Live Stripe payment
      const stripeUrl = response.data.url;
      try {
        if (window.top !== window.self) {
          window.top.location.href = stripeUrl;
          return;
        }
      } catch (e) { logger.error('Cross-origin iframe redirect blocked:', e); }
      
      try {
        const win = window.open(stripeUrl, '_blank');
        if (!win || win.closed) {
          window.location.href = stripeUrl;
        }
      } catch (e) {
        window.location.href = stripeUrl;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const requestContact = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/contact-request/${id}`, {});
      setContactRequestStatus(res.data.status);
      toast.success('Contact request submitted for admin review');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const generateAIValuation = async () => {
    if (!user) { toast.error('Please login'); navigate('/login'); return; }
    setAiLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/properties/${id}/ai-valuation`, {});
      setAiValuation(response.data.valuation);
      setShowAIModal(true);
      toast.success('AI Valuation generated!');
    } catch (error) { toast.error('Failed to generate AI valuation'); }
    finally { setAiLoading(false); }
  };

  const handleBooking = async () => {
    if (!selectedDate || !bookingTime) { toast.error('Select date and time'); return; }
    setBookingLoading(true);
    try {
      await axios.post(`${API_URL}/api/bookings`, {
        property_id: id, date: selectedDate.toISOString().split('T')[0], time: bookingTime, message: bookingMessage
      });
      setShowBookingModal(false);
      toast.success(`Viewing booked for ${selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} at ${bookingTime}. Check your dashboard for updates.`, { duration: 6000 });
      // Refresh access status
      checkAccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to book. Please try again.'); }
    finally { setBookingLoading(false); }
  };

  const formatPrice = (price) => formatConvertedPrice(price, propertyCurrency, selectedCountry, exchangeRates);

  const allImages = property ? [...(property.images || []), ...(property.panoramic_image ? [property.panoramic_image] : [])] : [];
  const DETAIL_FALLBACKS = {
    land: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&auto=format&fit=crop',
    commercial: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&auto=format&fit=crop',
    studio: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200&auto=format&fit=crop',
    apartment: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&auto=format&fit=crop',
    penthouse: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&auto=format&fit=crop',
    cottage: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=1200&auto=format&fit=crop',
    bungalow: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&auto=format&fit=crop',
  };
  const defaultImage = DETAIL_FALLBACKS[property?.property_type] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&auto=format&fit=crop';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Header />
        <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 text-[#E07A3A] animate-spin" /></div>
      </div>
    );
  }

  if (!property) return null;

  const hasLocation = property.latitude && property.longitude;
  const propertyUrl = window.location.href;

  const refTag = property.ref_number ? ` | Ref: ${property.ref_number}` : '';

  const shareMessages = {
    whatsapp: `🏠 *${property.title}*${refTag}\n📍 ${property.city}, ${property.state}\n💰 ${formatPrice(property.price)}${property.listing_type === 'rent' ? '/mo' : ''}\n🛏 ${property.bedrooms} Bed · 🚿 ${property.bathrooms} Bath · 📐 ${property.area_sqft?.toLocaleString('en-GB')} sqft\n${property.panoramic_image ? '🔄 360° Virtual Tour Available!\n' : ''}👉 View: ${propertyUrl}`,
    instagram: `🏠 ${property.title}${refTag}\n📍 ${property.city}\n💰 ${formatPrice(property.price)}${property.listing_type === 'rent' ? '/mo' : ''}\n${property.bedrooms} Bed | ${property.bathrooms} Bath | ${property.area_sqft?.toLocaleString('en-GB')} sqft\n${property.panoramic_image ? '🔄 360° Tour Available\n' : ''}#Shopperden #${property.city.replace(/\s/g, '')}RealEstate #PropertyForSale #LuxuryHomes`,
    facebook: `Check out this amazing property!\n\n${property.title}${refTag}\n${property.city}, ${property.state} — ${formatPrice(property.price)}${property.listing_type === 'rent' ? '/mo' : ''}\n${property.bedrooms} Bedrooms · ${property.bathrooms} Bathrooms · ${property.area_sqft?.toLocaleString('en-GB')} sqft\n${property.panoramic_image ? '360° virtual tour available!\n' : ''}\n${propertyUrl}`,
    twitter: `🏠 ${property.title}${refTag} | ${property.city}\n💰 ${formatPrice(property.price)}${property.listing_type === 'rent' ? '/mo' : ''}\n🛏 ${property.bedrooms}B ${property.bathrooms}Ba · ${property.area_sqft?.toLocaleString('en-GB')} sqft\n${property.panoramic_image ? '🔄 360° Tour\n' : ''}👉 ${propertyUrl}`,
    sms: `Hi! Check this property: ${property.title}${refTag} in ${property.city} for ${formatPrice(property.price)}. View here: ${propertyUrl}`
  };

  const fallbackCopy = (text, label) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(`${label} text copied!`);
    } catch (err) {
      window.prompt('Copy this text:', text);
    }
  };

  const copyShareText = (platform) => {
    const label = platform.charAt(0).toUpperCase() + platform.slice(1);
    fallbackCopy(shareMessages[platform], label);
  };

  const openShareLink = (platform) => {
    const encodedMsg = encodeURIComponent(shareMessages[platform]);
    const encodedUrl = encodeURIComponent(propertyUrl);
    const links = {
      whatsapp: `https://wa.me/?text=${encodedMsg}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodeURIComponent(shareMessages.facebook)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodedMsg}`,
      sms: `sms:?body=${encodedMsg}`
    };
    if (links[platform]) {
      try {
        window.open(links[platform], '_blank');
      } catch (e) {
        fallbackCopy(shareMessages[platform], platform);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      
      {/* 360° Viewer overlay */}
      {showPanoramic && property.panoramic_image && (
        <PanoramicViewer imageUrl={property.panoramic_image} onClose={() => setShowPanoramic(false)} />
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Image Gallery */}
        <div className="relative h-[50vh] md:h-[60vh] overflow-hidden mb-8 shadow-lg">
          <img src={allImages[currentImageIndex] || defaultImage} alt={property.title}
            className="w-full h-full object-cover" onError={(e) => { e.target.src = defaultImage; }} />
          
          {allImages.length > 1 && (
            <>
              <button onClick={() => setCurrentImageIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 shadow-lg flex items-center justify-center text-slate-700 hover:bg-white" data-testid="prev-image-btn">
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button onClick={() => setCurrentImageIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 shadow-lg flex items-center justify-center text-slate-700 hover:bg-white" data-testid="next-image-btn">
                <ChevronRight className="h-6 w-6" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {allImages.map((_, idx) => (
                  <button key={`img-dot-${idx}`} onClick={() => setCurrentImageIndex(idx)}
                    className={`w-3 h-3 ${idx === currentImageIndex ? 'bg-[#E07A3A]' : 'bg-white/70'}`} />
                ))}
              </div>
            </>
          )}

          {/* Top-left badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            <span className={`px-4 py-2 text-sm font-semibold ${property.listing_type === 'sale' ? 'bg-[#E07A3A] text-white' : 'bg-white/90 text-[#1A1A1A]'}`}>
              {property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}
            </span>
            {property.panoramic_image && (
              <button onClick={() => setShowPanoramic(true)}
                className="px-4 py-2 text-sm font-semibold bg-white/90 text-[#1A1A1A] hover:bg-white flex items-center gap-1 cursor-pointer" data-testid="open-360-btn">
                <Maximize className="h-4 w-4" /> 360° View
              </button>
            )}
          </div>

          {/* Top-right actions */}
          <div className="absolute top-4 right-4 flex gap-2">
            <button onClick={toggleFavorite}
              className={`w-12 h-12 flex items-center justify-center shadow-lg ${isFavorite ? 'bg-red-500 text-white' : 'bg-white/90 text-slate-600 hover:bg-white'}`}
              data-testid="favorite-btn">
              {isFavorite ? <HeartOff className="h-5 w-5" /> : <Heart className="h-5 w-5" />}
            </button>
            <button onClick={() => setShowSharePanel(!showSharePanel)}
              className="w-12 h-12 bg-white/90 shadow-lg flex items-center justify-center text-slate-600 hover:bg-white" data-testid="share-btn">
              <Share2 className="h-5 w-5" />
            </button>
          </div>

          {/* Share Panel */}
          {showSharePanel && (
            <div className="absolute top-20 right-4 bg-white shadow-2xl border border-slate-200 z-50 w-80 animate-in slide-in-from-top-2" data-testid="share-panel">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-semibold text-[#1A1A1A] text-sm tracking-wider">SHARE PROPERTY</h3>
                <button onClick={() => setShowSharePanel(false)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
              </div>
              <div className="p-3 space-y-2">
                {[
                  { id: 'whatsapp', label: 'WhatsApp', color: 'bg-green-500', icon: <MessageCircle className="h-4 w-4" /> },
                  { id: 'instagram', label: 'Instagram', color: 'bg-gradient-to-r from-purple-500 to-pink-500', icon: <Copy className="h-4 w-4" /> },
                  { id: 'facebook', label: 'Facebook', color: 'bg-blue-600', icon: <Share2 className="h-4 w-4" /> },
                  { id: 'twitter', label: 'X (Twitter)', color: 'bg-black', icon: <Share2 className="h-4 w-4" /> },
                  { id: 'sms', label: 'SMS', color: 'bg-[#E07A3A]', icon: <MessageCircle className="h-4 w-4" /> },
                ].map(({ id, label, color, icon }) => (
                  <div key={id} className="flex items-center gap-2">
                    <button onClick={() => openShareLink(id)}
                      className={`flex-1 flex items-center gap-3 px-4 py-2.5 ${color} text-white text-sm font-medium tracking-wider hover:opacity-90 transition-opacity`}
                      data-testid={`share-${id}`}>
                      {icon} {label}
                    </button>
                    <button onClick={() => copyShareText(id)}
                      className="p-2.5 border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-[#E07A3A] transition-colors"
                      title={`Copy ${label} text`}
                      data-testid={`copy-${id}`}>
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-slate-100">
                <button onClick={() => fallbackCopy(propertyUrl, 'Link')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium tracking-wider hover:bg-slate-50 transition-colors"
                  data-testid="copy-link">
                  <Copy className="h-4 w-4" /> COPY LINK
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Owner-claim banner — shown only for harvested/aggregate listings that aren't yet claimed. */}
            <OwnerClaimBanner property={property} />
            {/* Title & Price with Currency Switcher */}
            <div>
              <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#1A1A1A] mb-2 font-semibold">{property.title}</h1>
              {property.ref_number && (
                <span className="inline-block bg-[#0F172A] text-white text-xs tracking-[0.15em] uppercase font-bold px-3 py-1 mb-3" data-testid="property-ref-number">
                  {property.ref_number}
                </span>
              )}
              {/* Property badges row */}
              <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="property-badges">
                <PropertyTypeBadge type={property.property_type} />
                {property.tenure && <TenureBadge tenure={property.tenure} />}
                {property.epc_rating && <EpcBar rating={property.epc_rating} />}
                {property.council_tax_band && <CouncilTaxBadge band={property.council_tax_band} />}
                {property.furnished_status && <FurnishedBadge status={property.furnished_status} />}
              </div>
              {/* Trust badges — Pulse-Verified + Google-Verified pills (Wave 2 Sales) */}
              <ListingBadges listingId={id} dataTestId="property-trust-badges" />
              <div className="flex items-center gap-2 text-[#1A1A1A]/60 mb-4 text-base font-medium">
                <MapPin className="h-5 w-5 text-[#E07A3A]" />
                {property.address_hidden ? (
                  <span className="text-[#1A1A1A]/40 italic">{property.city || 'Location available after verification'}</span>
                ) : (
                  <span>{property.address}, {property.city}, {property.state} {property.zip_code}</span>
                )}
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="font-['Playfair_Display'] text-3xl text-[#E07A3A] font-semibold">
                  {formatPrice(property.price)}
                  {property.listing_type === 'rent' && <span className="text-lg font-normal text-[#1A1A1A]/50">/mo</span>}
                </span>
                {propertyCurrency !== ({ UK: 'GBP', IN: 'INR', CA: 'CAD' }[selectedCountry] || 'GBP') && (
                  <p className="text-xs text-slate-400 mt-1" data-testid="original-price">
                    Listed: {propSymbol}{Number(property.price).toLocaleString('en-GB')} ({propertyCurrency})
                  </p>
                )}
              </div>

              {/* Pulse Investment Report upsell — highest-context conversion point */}
              {property.city && (
                <Link
                  to={`/free-valuation?city=${encodeURIComponent(property.city)}&country=${property.country || selectedCountry}`}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#0A0A0C] to-[#1A1A1A] text-[#D4AF37] hover:from-[#D4AF37] hover:to-[#D4AF37] hover:text-[#0A0A0C] text-xs font-bold tracking-widest uppercase transition-all"
                  data-testid="property-pulse-report-cta"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Get an Independent Pulse Report on {property.city}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: <Bed className="h-6 w-6" />, value: property.bedrooms, label: 'Bedrooms' },
                { icon: <Bath className="h-6 w-6" />, value: property.bathrooms, label: 'Bathrooms' },
                { icon: <Square className="h-6 w-6" />, value: formatAreaSize(property.area_sqft, selectedCountry), label: '' },
              ].map((s, i) => (
                <div key={s.label} className="luxury-card p-5 text-center">
                  <div className="text-[#E07A3A] mx-auto mb-2 flex justify-center">{s.icon}</div>
                  <span className="text-2xl font-['Playfair_Display'] text-[#1A1A1A] font-semibold">{s.value}</span>
                  <p className="text-[#1A1A1A]/50 text-sm font-medium">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Description */}
            <div className="luxury-card p-6">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4 font-semibold">Description</h2>
              <p className="text-[#1A1A1A]/70 leading-relaxed whitespace-pre-line text-base">{property.description}</p>
            </div>

            {/* Amenities */}
            {property.amenities?.length > 0 && (
              <div className="luxury-card p-6">
                <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4 font-semibold">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {property.amenities.map((a, i) => (
                    <span key={`amenity-${a}-${i}`} className="px-4 py-2 bg-[#FEF3EC] text-[#E07A3A] text-sm font-semibold">{a}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Tenure & Leasehold Details */}
            {(property.tenure || property.lease_years_remaining || property.ground_rent || property.service_charge) && (
              <div className="luxury-card p-6" data-testid="tenure-details-section">
                <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4 font-semibold">Tenure & Legal</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {property.tenure && (
                    <div className="p-4 bg-[#F8FAFC] border border-slate-100">
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Tenure</p>
                      <p className="text-lg font-semibold text-[#1A1A1A] capitalize">{property.tenure?.replace(/_/g, ' ')}</p>
                    </div>
                  )}
                  {property.lease_years_remaining && (
                    <div className="p-4 bg-[#F8FAFC] border border-slate-100">
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Lease Remaining</p>
                      <p className={`text-lg font-semibold ${property.lease_years_remaining < 80 ? 'text-red-600' : 'text-[#1A1A1A]'}`}>
                        {property.lease_years_remaining} years
                      </p>
                      {property.lease_years_remaining < 80 && (
                        <p className="text-xs text-red-500 mt-1">Short lease — may affect mortgage eligibility</p>
                      )}
                    </div>
                  )}
                  {property.ground_rent != null && property.ground_rent > 0 && (
                    <div className="p-4 bg-[#F8FAFC] border border-slate-100">
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Ground Rent</p>
                      <p className="text-lg font-semibold text-[#1A1A1A]">{formatPrice(property.ground_rent)}/yr</p>
                    </div>
                  )}
                  {property.service_charge != null && property.service_charge > 0 && (
                    <div className="p-4 bg-[#F8FAFC] border border-slate-100">
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Service Charge</p>
                      <p className="text-lg font-semibold text-[#1A1A1A]">{formatPrice(property.service_charge)}/yr</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rental Details */}
            {property.listing_type === 'rent' && (property.deposit_amount || property.available_from) && (
              <div className="luxury-card p-6" data-testid="rental-details-section">
                <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4 font-semibold">Rental Details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {property.deposit_amount && (
                    <div className="p-4 bg-[#F8FAFC] border border-slate-100">
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Deposit</p>
                      <p className="text-lg font-semibold text-[#1A1A1A]">{formatPrice(property.deposit_amount)}</p>
                    </div>
                  )}
                  {property.available_from && (
                    <div className="p-4 bg-[#F8FAFC] border border-slate-100">
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Available From</p>
                      <p className="text-lg font-semibold text-[#1A1A1A]">{new Date(property.available_from).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Floor Plans — multi-floor with legal disclaimer */}
            {(property.floor_plans?.length > 0 || property.floor_plan_images?.length > 0) && (
              <FloorPlansSection property={property} />
            )}

            {/* Map */}
            {hasLocation && (
              <div className="luxury-card p-6" data-testid="property-map">
                <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4 font-semibold">Location</h2>
                <div className="h-80 overflow-hidden" style={{ borderRadius: 0 }}>
                  <MapContainer 
                    center={[property.latitude, property.longitude]} 
                    zoom={14} 
                    scrollWheelZoom={false}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[property.latitude, property.longitude]}>
                      <Popup>
                        <strong>{property.title}</strong><br />
                        {property.address_hidden ? property.city : `${property.address}, ${property.city}`}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>
            )}

            {/* AI Valuation */}
            <div className="luxury-card p-6 border-2 border-[#FDDCBC]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#FEF3EC] flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-[#E07A3A]" />
                </div>
                <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">AI Premium Valuation</h2>
              </div>
              {aiValuation ? (
                <div className="space-y-4">
                  <p className="text-[#1A1A1A]/70 whitespace-pre-line line-clamp-4 text-base">{aiValuation}</p>
                  <Button onClick={() => setShowAIModal(true)} variant="outline" className="border-[#E07A3A]/30 text-[#E07A3A] hover:bg-[#FEF3EC] font-semibold" data-testid="view-ai-details-btn">View Full Analysis</Button>
                </div>
              ) : (
                <div>
                  <p className="text-[#1A1A1A]/60 mb-4 text-base font-medium">Get AI-powered premium profit insights and market valuation.</p>
                  <Button onClick={generateAIValuation} disabled={aiLoading} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] font-semibold" data-testid="generate-ai-valuation-btn">
                    {aiLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analyzing...</> : <><Sparkles className="mr-2 h-4 w-4" />Generate AI Valuation</>}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <PropertySidebar
            property={property}
            id={id}
            user={user}
            access={access}
            sellerContact={sellerContact}
            contactRequestStatus={contactRequestStatus}
            handlePayment={handlePayment}
            requestContact={requestContact}
            setShowBookingModal={setShowBookingModal}
            paymentLoading={paymentLoading}
            buyerFee={buyerFee}
            tenantFee={tenantFee}
            setReferralType={setReferralType}
            setShowReferralModal={setShowReferralModal}
          />
        </div>

        {/* Trusted Professionals for this property's city. Same paywall as
            Vault Unlock (£15.52). Auto-fills from Google Places harvester. */}
        {property?.city && (
          <div className="max-w-7xl mx-auto mt-8" data-testid="property-local-pros">
            <LocalProsPanel
              role="buyer"
              city={property.city}
              country={(property.country || 'UK').toUpperCase()}
              dataTestId={`property-local-pros-${id}`}
            />
          </div>
        )}
      </div>

      {/* AI Modal */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A1A1A]"><Sparkles className="h-5 w-5 text-[#E07A3A]" />AI Premium Valuation</DialogTitle>
            <DialogDescription className="text-[#1A1A1A]/50">Powered by advanced AI</DialogDescription>
          </DialogHeader>
          <div className="mt-4 p-4 bg-[#FEF3EC] max-h-[50vh] overflow-y-auto">
            <pre className="text-[#1A1A1A]/80 whitespace-pre-wrap font-sans text-sm leading-relaxed">{aiValuation}</pre>
          </div>
          <AIDisclaimer />
        </DialogContent>
      </Dialog>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1A1A1A]">Schedule Property Viewing</DialogTitle>
            <DialogDescription className="text-[#1A1A1A]/50">Select preferred date and time</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#1A1A1A]/70 mb-2 block font-semibold">Select Date</Label>
              <div className="flex justify-center bg-[#F8FAFC] p-2">
                <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} disabled={(date) => date < new Date()} />
              </div>
            </div>
            <div>
              <Label className="text-[#1A1A1A]/70 font-semibold">Preferred Time</Label>
              <Input type="time" value={bookingTime} onChange={(e) => setBookingTime(e.target.value)} className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12" data-testid="booking-time-input" />
            </div>
            <div>
              <Label className="text-[#1A1A1A]/70 font-semibold">Message (Optional)</Label>
              <Textarea value={bookingMessage} onChange={(e) => setBookingMessage(e.target.value)} placeholder="Any special requests..." className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A]" rows={2} data-testid="booking-message-input" />
            </div>
            <div className="p-2 bg-amber-50 border border-amber-200" data-testid="booking-disclaimer">
              <p className="text-xs text-amber-700 leading-relaxed">
                Shopperden arranges introductions only. Verify details independently before any commitment.
              </p>
            </div>
            <Button onClick={handleBooking} disabled={bookingLoading || !selectedDate || !bookingTime} className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 font-semibold" data-testid="confirm-booking-btn">
              {bookingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Booking'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Referral Modal */}
      <Dialog open={showReferralModal} onOpenChange={setShowReferralModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1A1A1A]">Find a {referralType ? referralType.charAt(0).toUpperCase() + referralType.slice(1) : ''}</DialogTitle>
            <DialogDescription className="text-[#1A1A1A]/50">We'll connect you with a verified {referralType} in your area</DialogDescription>
          </DialogHeader>
          <ReferralForm
            propertyId={id}
            serviceType={referralType}
            onSuccess={() => { setShowReferralModal(false); toast.success('Referral request submitted!'); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyDetailPage;
